import { UrlMatchResult, UrlSegment } from '@angular/router';
import { KeywordSearchType } from '../../../../models/browse-keyword-category';
import { SearchLevelEnum } from '../../../pages/gallery/asset-gallery/models/search-level-enum';

/** Single URL segment for browse-keywords detail pages: `/top-icons`, `/trending-icons-packs`. */
export type BrowseKeywordRouteMode = 'top' | 'trending' | 'search';

const BROWSE_KEYWORD_ROUTE_MODES: BrowseKeywordRouteMode[] = [
  'trending',
  'search',
  'top',
];

export const BROWSE_KEYWORD_PACKS_SUFFIX = '-packs';

/** Maps landing search-type pills to detail URL mode segments. */
export function browseModeFromSearchType(
  type: KeywordSearchType
): BrowseKeywordRouteMode {
  return type === 'Trending searches' ? 'trending' : 'top';
}

export function searchTypeFromBrowseMode(
  mode: BrowseKeywordRouteMode
): KeywordSearchType {
  return mode === 'trending' ? 'Trending searches' : 'Top searches';
}

/**
 * Display label only. Search links must keep the original keyword.
 * Lottie Animations uses "Animation". Other categories keep their category name.
 * Pack adds a trailing "Pack" without repeating words already present.
 */
export function formatBrowseKeywordLabel(
  keyword: string,
  categoryName: string,
  includePack = false
): string {
  const suffix = categoryKeywordSuffix(categoryName);
  let base = keyword.trim().replace(/\s+/g, ' ');
  if (includePack) {
    base = stripTrailingPhrase(base, 'Pack');
  }
  base = stripTrailingPhrase(base, suffix);

  const withCategory = suffix ? `${base} ${suffix}`.trim() : base;
  const label = includePack ? `${withCategory} Pack`.trim() : withCategory;
  return label.replace(/\s+/g, ' ');
}

function categoryKeywordSuffix(categoryName: string): string {
  const name = categoryName.trim();
  if (name.toLowerCase() === 'lottie animations') {
    return 'Animation';
  }
  return name;
}

function stripTrailingPhrase(text: string, phrase: string): string {
  const trimmedPhrase = phrase.trim();
  if (!text || !trimmedPhrase) {
    return text;
  }
  const pattern = new RegExp(`\\s+${escapeRegExp(trimmedPhrase)}$`, 'i');
  if (text.toLowerCase() === trimmedPhrase.toLowerCase()) {
    return '';
  }
  return text.replace(pattern, '').trim();
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function isBrowseKeywordRouteMode(
  value: string | null | undefined
): value is BrowseKeywordRouteMode {
  return !!value && BROWSE_KEYWORD_ROUTE_MODES.includes(value as BrowseKeywordRouteMode);
}

/** Strip optional `-packs` suffix and return the category kebab slug. */
export function parseBrowseCategorySlug(categorySegment: string): {
  categorySlug: string;
  searchLevel: SearchLevelEnum;
} {
  if (categorySegment.endsWith(BROWSE_KEYWORD_PACKS_SUFFIX)) {
    return {
      categorySlug: categorySegment.slice(0, -BROWSE_KEYWORD_PACKS_SUFFIX.length),
      searchLevel: SearchLevelEnum.Pack,
    };
  }
  return {
    categorySlug: categorySegment,
    searchLevel: SearchLevelEnum.Individual,
  };
}

/**
 * Matches one segment: `top-lottie-animations`, `trending-icons-packs`, `search-stickers`.
 * Exposes `browseMode` + `categorySlug` (`categorySlug` may still end with `-packs`).
 */
export function browseKeywordCategoryMatcher(
  segments: UrlSegment[]
): UrlMatchResult | null {
  if (segments.length !== 1) {
    return null;
  }

  const path = segments[0].path;
  for (const mode of BROWSE_KEYWORD_ROUTE_MODES) {
    const prefix = `${mode}-`;
    if (!path.startsWith(prefix) || path.length <= prefix.length) {
      continue;
    }

    const categorySlug = path.slice(prefix.length);
    if (!categorySlug || categorySlug === BROWSE_KEYWORD_PACKS_SUFFIX.slice(1)) {
      return null;
    }

    return {
      consumed: segments,
      posParams: {
        browseMode: new UrlSegment(mode, {}),
        categorySlug: new UrlSegment(categorySlug, {}),
      },
    };
  }

  return null;
}
